import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random().toString(36).substring(2, 5);
    setToasts((prev) => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast Render Container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm w-full pointer-events-none">
        {toasts.map((t) => {
          const typeStyles = {
            success: 'bg-slate-900/95 border-emerald-500/50 text-emerald-300',
            error: 'bg-slate-900/95 border-rose-500/50 text-rose-300',
            warning: 'bg-slate-900/95 border-amber-500/50 text-amber-300',
            info: 'bg-slate-900/95 border-[#9BB7A3]/50 text-[#9BB7A3]',
          };

          const icons = {
            success: <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />,
            error: <AlertCircle size={18} className="text-rose-400 shrink-0" />,
            warning: <AlertCircle size={18} className="text-amber-400 shrink-0" />,
            info: <Info size={18} className="text-[#9BB7A3] shrink-0" />,
          };

          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-center justify-between gap-3 p-4 rounded-xl border backdrop-blur-md shadow-2xl transition-all animate-fadeIn ${typeStyles[t.type] || typeStyles.info}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {icons[t.type]}
                <p className="text-xs sm:text-sm font-medium text-slate-100 leading-snug break-words">
                  {t.message}
                </p>
              </div>
              <button
                onClick={() => removeToast(t.id)}
                className="text-slate-400 hover:text-white p-1 rounded transition"
              >
                <X size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    return {
      success: console.log,
      error: console.error,
      info: console.info,
      warning: console.warn,
    };
  }
  return context;
}
